import React, { RefObject } from 'react';
import { FieldDefinition } from '../../utils/entityFieldDefinitions';
import CompactCardSelector from '../common/CompactCardSelector';
import { StyledInput } from '../styles/StyledInputs';

export interface EntityFieldFocusRefs {
  trusteeName: RefObject<HTMLInputElement>;
  firstName: RefObject<HTMLInputElement>;
  companyName: RefObject<HTMLInputElement>;
}

interface EntitySpecificFieldInputProps {
  field: FieldDefinition;
  value: string | number;
  error?: string;
  onChange: (fieldKey: string, value: string | number) => void;
  focusRefs: EntityFieldFocusRefs;
}

function getTextFieldRef(fieldKey: string, focusRefs: EntityFieldFocusRefs) {
  if (fieldKey === 'trusteeName') {
    return focusRefs.trusteeName;
  }
  if (fieldKey === 'firstName') {
    return focusRefs.firstName;
  }
  if (fieldKey === 'companyName') {
    return focusRefs.companyName;
  }
  return undefined;
}

export const EntitySpecificFieldInput: React.FC<EntitySpecificFieldInputProps> = ({
  field,
  value,
  error,
  onChange,
  focusRefs,
}) => {
  switch (field.type) {
    case 'select':
      return (
        <CompactCardSelector
          key={field.key}
          label={field.label}
          required={field.required}
          options={
            field.options?.map((option) => ({
              value: option.value,
              label: option.label,
            })) || []
          }
          value={typeof value === 'number' ? String(value) : value}
          onChange={(newValue) => onChange(field.key, newValue)}
          error={error}
          helperText={field.helperText}
        />
      );

    case 'textarea':
      return (
        <StyledInput
          key={field.key}
          label={field.label}
          value={value}
          onChange={(e) => onChange(field.key, e.target.value)}
          error={!!error}
          helperText={error || field.helperText}
          required={field.required}
          multiline
          rows={3}
          fullWidth
          placeholder={field.placeholder}
        />
      );

    case 'number':
      return (
        <StyledInput
          key={field.key}
          label={field.label}
          value={value}
          onChange={(e) => onChange(field.key, e.target.value)}
          error={!!error}
          helperText={error || field.helperText}
          required={field.required}
          type="number"
          fullWidth
          placeholder={field.placeholder}
        />
      );

    default:
      return (
        <StyledInput
          key={field.key}
          ref={getTextFieldRef(field.key, focusRefs)}
          label={field.label}
          value={value}
          onChange={(e) => onChange(field.key, e.target.value)}
          error={!!error}
          helperText={error || field.helperText}
          required={field.required}
          type={field.type === 'date' ? 'date' : 'text'}
          fullWidth
          placeholder={field.placeholder}
          InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
        />
      );
  }
};
